/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Mobile primary navigation. Shares the exact V12 community CSS.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CommunityNavBadge from "./CommunityNavBadge";

export default function BioIndexUploadMobileNav({ onSearch, onVoiceSearch }) {
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreMenuRef = useRef(null);

  useEffect(() => {
    if (!exploreOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!exploreMenuRef.current?.contains(event.target)) {
        setExploreOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setExploreOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [exploreOpen]);

  const openSearch = () => {
    setExploreOpen(false);
    onSearch?.();
  };
  const openVoiceSearch = () => {
    setExploreOpen(false);
    onVoiceSearch?.();
  };

  return (
    <nav className="bioindex-m-bottom-bar" aria-label="Primary navigation">
      <div className="bioindex-m-bottom-grid">
        <Link className="bioindex-m-bottom-item" to="/" aria-label="Home">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 10.8 12 3l9 7.8V21h-6v-7H9v7H3Z" />
          </svg>
          <span>Home</span>
        </Link>

        <div className="bioindex-m-explore-wrap" ref={exploreMenuRef}>
          <button
            type="button"
            className={`bioindex-m-bottom-item bioindex-m-explore-trigger${exploreOpen ? " bioindex-m-explore-active" : ""}`}
            onClick={() => setExploreOpen((open) => !open)}
            aria-expanded={exploreOpen}
            aria-controls="bioindex-upload-explore-menu"
            aria-label="Explore or Search"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="m15.9 8.1-2.2 5.5-5.5 2.2 2.2-5.5Z" />
            </svg>
            <span>Explore</span>
          </button>
          {exploreOpen && (
            <div id="bioindex-upload-explore-menu" className="bioindex-m-explore-menu" role="group" aria-label="Explore options">
              <Link to="/explore" className="bioindex-m-explore-choice" onClick={() => setExploreOpen(false)}>
                <span className="bioindex-m-explore-choice-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="8.6" />
                    <path d="m15.6 8.4-2.2 5-5 2.2 2.2-5Z" />
                  </svg>
                </span>
                <span>Explore taxonomy</span>
              </Link>
              <button type="button" className="bioindex-m-explore-choice" onClick={openSearch}>
                <span className="bioindex-m-explore-choice-icon" aria-hidden="true">
                  <img src="/Search.png" alt="" />
                </span>
                <span>Search BioIndex</span>
              </button>
              <button type="button" className="bioindex-m-explore-choice" onClick={openVoiceSearch}>
                <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" /></svg></span>
                <span>Voice Search</span>
              </button>
            </div>
          )}
        </div>

        <div className="bioindex-m-bottom-item bioindex-m-add bioindex-m-current" aria-current="page" aria-label="Add Photos">
          <span className="bioindex-m-add-circle" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
              <circle cx="12" cy="13" r="3.4" />
            </svg>
          </span>
          <span>Add Photos</span>
        </div>

        <Link className="bioindex-m-bottom-item" to="/profile" aria-label="Profile">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="7.5" r="4" />
            <path d="M4.5 21v-2.7a7.5 7.5 0 0 1 15 0V21" />
          </svg>
          <span>Profile</span>
        </Link>
        <Link className="bioindex-m-bottom-item" to="/forum" aria-label="Community">
          <span className="bioindex-m-current-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <circle cx="9" cy="8" r="3" />
              <path d="M2.5 20v-2a6.5 6.5 0 0 1 13 0v2M17 5.5a3 3 0 0 1 0 5.5M17 14a5.5 5.5 0 0 1 4 5.5" />
            </svg>
            <CommunityNavBadge />
          </span>
          <span>Community</span>
        </Link>
      </div>
    </nav>
  );
}
